import type { FC } from 'react';
import type { MessageDescriptor } from 'react-intl';
import React, { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import { styled } from '@compiled/react';

import Tooltip from '@atlaskit/tooltip/Tooltip';
import { token } from '@atlaskit/tokens';

import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { Link } from '@confluence/route-manager/entry-points/Link.ts';
import { TimeAgo } from '@confluence/time-ago';
import { fg } from '@confluence/feature-gating';
import { LivePagePreloader } from '@confluence/live-page-preloader/entry-points/LivePagePreloader.ts';
import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';

import { i18n } from '../i18n.js';
import { NarrowPageCardLayout } from '../layouts/NarrowPageCardLayout.tsx';

import { StatusLozenge } from './StatusLozenge.tsx';
import { EmojiContentIcon, getTitle } from './EmojiContentIcon.tsx';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceLinkWrapper = styled.div({
	zIndex: 1,
	display: 'flex',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceLink = styled(Link)({
	position: 'relative',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LastModifiedWrapper = styled.div({
	font: token('font.body.small'),
	verticalAlign: 'baseline',
});

const titleSelector = 'home.recents.Title';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleAndStatus = styled.div({
	display: 'flex',
	/* CSS specificity fix. turns default blue if it goes directly in the a tag */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	[`> [data-styled-selector="${titleSelector}"]`]: {
		color: token('color.text'),
		'&:hover': {
			color: token('color.text.subtle'),
			textDecoration: 'none',
		},
		'&:focus, &:focus-visible': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			outlineOffset: '-1px',
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.a<{ 'data-styled-selector'?: string }>({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	position: 'relative',
	whiteSpace: 'nowrap',
	color: token('color.text'),
});
Title.defaultProps = {
	'data-styled-selector': titleSelector,
};

type NarrowPageCard = {
	id: string;
	type: string;
	subType: string | null;
	status?: string;
	links: {
		webui: string;
	};
	title: {
		text: string;
		emoji: string;
	};
	space: {
		spaceKey: string;
		name: string;
	};
	lastAction: {
		when?: string;
		action?: string;
	};
};

type NarrowPageCardViewProps = {
	content: NarrowPageCard;
	onClick?: () => void;
	onSpaceClick?: () => void;
	action?: string;
	lastActionI18n?: MessageDescriptor;
};

export const NarrowPageCardView: FC<NarrowPageCardViewProps> = ({
	content,
	onClick,
	onSpaceClick,
	action,
	lastActionI18n,
}) => {
	const isLivePage =
		content?.type === 'page' && content?.subType === 'live' && content?.status !== 'archived';

	const EMOJI_CONTENT_TYPES = fg('confluence_frontend_whiteboard_emoji_titles')
		? ['page', 'blogpost', 'embed', 'whiteboard', 'slide']
		: ['page', 'blogpost', 'embed'];
	const url = `${CONTEXT_PATH}${content?.links?.webui}`;

	const isEmojiContentType = content?.type && EMOJI_CONTENT_TYPES.includes(content.type);
	const emoji = content?.title?.emoji;
	const title = isEmojiContentType
		? getTitle(content?.title?.text, Boolean(emoji))
		: content?.title?.text;

	const icon = (
		<EmojiContentIcon
			emoji={emoji}
			contentType={content?.type || ''}
			subType={content?.subType || ''}
		/>
	);

	const primary = (
		<TitleAndStatus>
			<Tooltip content={title || <FormattedMessage {...i18n.contentUntitled} />} position="bottom">
				{(tooltipProps) => (
					<Title href={url} tabIndex={-1} aria-hidden {...tooltipProps}>
						{title || <FormattedMessage {...i18n.contentUntitled} />}
					</Title>
				)}
			</Tooltip>
			<StatusLozenge status={content?.status || ''} />
		</TitleAndStatus>
	);

	const space = content?.space;
	const spaceKey = space?.spaceKey;
	const spaceName = space?.name || '';
	const secondary =
		spaceName &&
		(spaceKey ? (
			<SpaceLinkWrapper>
				<SpaceLink href={SPACE_OVERVIEW.toUrl({ spaceKey })} onClick={onSpaceClick}>
					{spaceName}
				</SpaceLink>
			</SpaceLinkWrapper>
		) : (
			spaceName
		));
	const lastModifiedComponent = content?.lastAction?.when && (
		<LastModifiedWrapper>
			{lastActionI18n && (
				<Fragment>
					<FormattedMessage {...lastActionI18n} />{' '}
				</Fragment>
			)}
			<TimeAgo date={content.lastAction.when} />
		</LastModifiedWrapper>
	);

	const preloadOptions = SPACE_OVERVIEW.match(url)
		? PreloadingPresets.onlyJSAndCriticalData
		: PreloadingPresets.onlyJS;

	return (
		<LivePagePreloader isLivePage={isLivePage}>
			<HoverPreloader name="narrow-page-card" preloadUrl={url} preloadOptions={preloadOptions}>
				<NarrowPageCardLayout
					icon={icon}
					primary={primary}
					secondary={secondary}
					lastModified={lastModifiedComponent}
					actions={action}
					url={url}
					onClick={onClick}
					ariaLabel={title || <FormattedMessage {...i18n.contentUntitled} />}
					testId="narrow-page-card"
				/>
			</HoverPreloader>
		</LivePagePreloader>
	);
};
